// Phase H19 — per-item resolution picker shown in the items table when
// parent ReturnRequest.resolutionType === "MIXED".

import { Button, Group, NumberInput, Select, Text, Tooltip } from "@mantine/core";
import { showNotification } from "@dehub/theme";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ReturnApi } from "../../apis";
import type { RefundMethod, ResolutionType } from "../../apis/return";
import { currencyFormat } from "../../utils/currency";

// ── Constants ──

type ItemResolutionType = Exclude<ResolutionType, "MIXED">;

type TFn = (k: string, o?: Record<string, unknown>) => string;

const RESOLUTION_CODES: ItemResolutionType[] = ["REFUND_MONEY", "REPLACEMENT", "NEXT_ORDER_DISCOUNT", "NONE"];
const REFUND_METHOD_CODES: RefundMethod[] = ["BANK_TRANSFER", "INVOICE_DEDUCTION", "CASH", "CREDIT_BALANCE"];

const resolutionOptions = (t: TFn): { value: ItemResolutionType; label: string }[] =>
  RESOLUTION_CODES.map((value) => ({ value, label: t(`mixedResolution.resolution.${value}`) }));

const refundMethodOptions = (t: TFn): { value: RefundMethod; label: string }[] =>
  REFUND_METHOD_CODES.map((value) => ({ value, label: t(`mixedResolution.refundMethod.${value}`) }));

const TERMINAL_STATUSES = ["COMPLETED", "PARTIALLY_COMPLETED", "REJECTED", "CANCELLED"];

// ── Props ──

interface IProps {
  returnRequestId: string;
  returnItemId: string;
  itemQuantity: number;
  itemUnitPrice: number;
  initial: {
    itemResolutionType: string | null;
    itemRefundMethod: string | null;
    itemRefundAmount: number | null;
  };
  parentResolutionType: string | null;
  parentStatus: string;
  onSaved: () => void;
}

// ── Component ──

export function MixedResolutionPicker ({
  returnRequestId,
  returnItemId,
  itemQuantity,
  itemUnitPrice,
  initial,
  parentResolutionType,
  parentStatus,
  onSaved
}: IProps){
  const { t } = useTranslation("returns");
  const [resolutionType, setResolutionType] = useState<ItemResolutionType | null>(
    initial.itemResolutionType as ItemResolutionType | null
  );
  const [refundMethod, setRefundMethod] = useState<RefundMethod | null>(
    initial.itemRefundMethod as RefundMethod | null
  );
  const [refundAmount, setRefundAmount] = useState<number | string>(
    initial.itemRefundAmount ?? ""
  );
  const [saving, setSaving] = useState(false);

  const isEditable =
    parentResolutionType === "MIXED" &&
    !TERMINAL_STATUSES.includes(parentStatus);

  // ── Read-only display ──

  if (!isEditable){
    const reason = TERMINAL_STATUSES.includes(parentStatus)
      ? t("mixedResolution.notEditableStatus", { status: t(`status.${parentStatus}`, { defaultValue: "-" }) })
      : t("mixedResolution.notEditableParent");
    return (
      <Tooltip label={reason} withArrow>
        <Text size="sm" c="dimmed">
          {initial.itemResolutionType ? t(`resolutionType.${initial.itemResolutionType}`, { defaultValue: "-" }) : "-"}
          {initial.itemRefundMethod ? ` · ${t(`refundMethod.${initial.itemRefundMethod}`, { defaultValue: "-" })}` : ""}
          {initial.itemRefundAmount != null
            ? ` · ${currencyFormat(Number(initial.itemRefundAmount))}`
            : ""}
        </Text>
      </Tooltip>
    );
  }

  // ── Save ──

  const handleSave = async () => {
    setSaving(true);
    try {
      const amount =
        typeof refundAmount === "number"
          ? refundAmount
          : refundAmount === ""
          ? null
          : Number(refundAmount);

      await ReturnApi.setItemResolution(returnRequestId, returnItemId, {
        itemResolutionType: resolutionType ?? null,
        itemRefundMethod  : refundMethod ?? null,
        itemRefundAmount  : amount
      });
      showNotification({ color: "green", title: t("toast.success"), message: t("mixedResolution.saved") });
      onSaved();
    } catch (e){
      showNotification({ color: "red", title: t("toast.error"), message: (e as Error).message });
    } finally {
      setSaving(false);
    }
  };

  const maxAmount = itemQuantity * itemUnitPrice;

  // ── Editor ──

  return (
    <Group gap="xs" wrap="nowrap" align="flex-end">
      <Select
        size="sm"
        placeholder={t("filters.resolutionLabel")}
        data={resolutionOptions(t)}
        value={resolutionType}
        onChange={(v) => {
          setResolutionType(v as ItemResolutionType | null);
          if (v !== "REFUND_MONEY"){
            setRefundMethod(null);
            setRefundAmount("");
          }
        }}
        clearable
        w={140}
      />

      {resolutionType === "REFUND_MONEY" ? (
        <>
          <Select
            size="sm"
            placeholder={t("mixedResolution.method")}
            data={refundMethodOptions(t)}
            value={refundMethod}
            onChange={(v) => setRefundMethod(v as RefundMethod | null)}
            w={150}
          />
          <NumberInput
            size="sm"
            placeholder={t("detail.section.amount")}
            min={0}
            max={maxAmount}
            value={refundAmount}
            onChange={setRefundAmount}
            w={110}
            thousandSeparator=","
          />
        </>
      ) : null}

      <Button size="sm" loading={saving} onClick={handleSave}>
        {t("resolutionPanel.save")}
      </Button>
    </Group>
  );
}
